import { useCallback, useEffect, useRef, useState } from "react";

// A <video> plays straight from the bucket: /files/:key?redirect=1 answers with a presigned URL
// and the browser fetches the bytes in ranges from Spaces itself. The nyc3 bucket intermittently
// answers one of those range requests with a 503 ("upstream connect error"), on any object, old or
// new — measured at 13–25% of requests on 2026-10-01. A media element never retries: a single
// failed range ends playback, and a video whose first range fails never shows at all. Uploads did
// not notice because every upload step retries; playback had nothing.
//
// So go again, from a fresh redirect. The retry parameter makes the browser treat it as a new
// resource rather than reuse what it already has for the failed one, and the backend ignores it.
export const MEDIA_RETRY_DELAYS_MS = [1000, 3000, 8000];

export function retryUrl(url, attempt) {
    if (!attempt) {
        return url;
    }
    return url + (url.includes("?") ? "&" : "?") + "retry=" + attempt;
}

// Retries a media element's source after an error, keeping where playback was. `onGiveUp` runs
// once the retries are spent, with the element's last error. The count starts again whenever the
// source changes, and whenever the element gets as far as being able to play: a long review that
// hits a bad range now and then should recover each time, while a file this browser cannot decode
// never gets that far and so gives up after the last delay.
export default function useMediaRetry(mediaRef, src, { onGiveUp, onBeforeRetry, delays = MEDIA_RETRY_DELAYS_MS } = {}) {
    const attemptRef = useRef(0);
    const timerRef = useRef(null);
    const [retrying, setRetrying] = useState(false);

    // Read when they run rather than made dependencies, so a parent passing new callbacks on every
    // render does not restart anything.
    const callbacksRef = useRef({ onGiveUp, onBeforeRetry });
    callbacksRef.current = { onGiveUp, onBeforeRetry };

    const clearTimer = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    useEffect(() => {
        attemptRef.current = 0;
        setRetrying(false);
        return clearTimer;
    }, [src, clearTimer]);

    const reload = useCallback((attempt) => {
        const media = mediaRef.current;
        if (!media || !src) {
            return;
        }
        const resumeAt = media.currentTime || 0;
        const wasPlaying = !media.paused;
        callbacksRef.current.onBeforeRetry && callbacksRef.current.onBeforeRetry(media);

        const restore = () => {
            media.removeEventListener("loadedmetadata", restore);
            if (resumeAt) {
                media.currentTime = resumeAt;
            }
            if (wasPlaying) {
                media.play().catch(e => console.warn("Could not resume the video after reloading it.", e));
            }
        };
        media.addEventListener("loadedmetadata", restore);
        media.src = retryUrl(src, attempt);
        media.load();
    }, [mediaRef, src]);

    const onError = useCallback(() => {
        const media = mediaRef.current;
        if (!media || !src || timerRef.current) {
            return;
        }
        const attempt = attemptRef.current + 1;
        if (attempt > delays.length) {
            setRetrying(false);
            console.error(`Gave up loading ${src} after ${delays.length} retries.`, media.error);
            callbacksRef.current.onGiveUp && callbacksRef.current.onGiveUp(media.error);
            return;
        }
        attemptRef.current = attempt;
        setRetrying(true);
        console.warn(`Could not load ${src} (attempt ${attempt}); trying again in ${delays[attempt - 1]}ms.`, media.error);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            reload(attempt);
        }, delays[attempt - 1]);
    }, [mediaRef, src, delays, reload]);

    // The element got far enough to play, so whatever failed before has recovered.
    const onCanPlay = useCallback(() => {
        attemptRef.current = 0;
        setRetrying(false);
    }, []);

    // A fresh set of retries on demand, for a "Try again" after giving up.
    const retryNow = useCallback(() => {
        clearTimer();
        attemptRef.current = 0;
        setRetrying(true);
        reload(Date.now());
    }, [clearTimer, reload]);

    return { onError, onCanPlay, retrying, retryNow };
}
